import { VideoMakerData } from "components/video-maker-block/video-maker-block.data";
import { getSubjectInBahasa } from "./utils";
import { IVideoMakerData } from "components/video-maker-block/video-maker-block.types";

const UNIDENTIFIED = "unidentified";

type BreadCrumbItem = {
  url: string;
  text: string;
};

export type BreadCrumbDataParams = {
  subject: string;
  subjectSlug?: string;
  topic: string;
  topicSlug: string;
  section: string;
  sectionSlug: string;
  chapter: string;
  chapterSlug: string;
  grade: string;
  gradeSlug: string;
  gradeGroup: string;
};

const generateBreadCrumbData = ({
  subject,
  topic,
  topicSlug,
  section,
  sectionSlug,
  grade,
  gradeGroup,
  gradeSlug,
  chapter,
  chapterSlug
}: BreadCrumbDataParams): Array<BreadCrumbItem> => {
  const subjectInBahasa = getSubjectInBahasa(subject);
  const subjectSlugInBahasa = getSubjectInBahasa(subject);

  return [
    {
      url: "https://colearn.id/tanya",
      text: subjectInBahasa
    },
    {
      url: `https://colearn.id/tanya/section/${gradeSlug}/${subjectSlugInBahasa}/${sectionSlug}`,
      text: `${section} Kelas ${grade} ${gradeGroup}`
    },
    {
      url: `https://colearn.id/tanya/chapter/${gradeSlug}/${subjectSlugInBahasa}/${chapterSlug}`,
      text: chapter
    },
    {
      url: `https://colearn.id/tanya/topic/${gradeSlug}/${subjectSlugInBahasa}/${topicSlug}`,
      text: topic
    }
  ];
};

const generateBreadCrumbSchema = (
  breadCrumbData: Array<BreadCrumbItem>
): Record<string, unknown> => {
  const breadCrumbSchema = breadCrumbData.map((data, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@id": data.url,
      name: data.text
    }
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadCrumbSchema
  };
};

export const getBreadCrumbDetails = (
  params: BreadCrumbDataParams
): [Array<BreadCrumbItem>, Record<string, unknown>] => {
  const breadCrumbData: Array<BreadCrumbItem> = generateBreadCrumbData(params);
  const schema = generateBreadCrumbSchema(breadCrumbData);

  return [breadCrumbData, schema];
};

export const getDeviceMemory = (): [string, string] => {
  type memoryCohorts = "0.25" | "0.5" | "1" | "2" | "4" | "8";

  type Mapping = {
    [cohort in memoryCohorts]: string;
  };

  const mapping: Mapping = {
    "0.25": "256MB",
    "0.5": "512MB-1GB",
    "1": "2GB",
    "2": "2GB-3GB",
    "4": "4GB-6GB",
    "8": "8GB-32GB"
  };

  let deviceMemory = null;
  let expectedMemoryRange = "";

  // Experimental feature `navigator.deviceMemory`
  // hence still not available with general types
  // @ts-ignore
  if (navigator && navigator.deviceMemory) {
    // @ts-ignore
    deviceMemory = navigator.deviceMemory;

    if (Object.keys(mapping).includes(deviceMemory.toString())) {
      expectedMemoryRange = mapping[deviceMemory as memoryCohorts];
    } else {
      expectedMemoryRange = "unidentified";
    }
  }

  return [deviceMemory, expectedMemoryRange];
};

export const isVideoOfCurrentGrade = (
  videoGrade: string[],
  grade: number
): boolean => {
  if (videoGrade && videoGrade.length > 0) {
    return videoGrade.includes(grade.toString()) ? true : false;
  } else {
    return false;
  }
};

export const getDeviceConnectionType = (): string => {
  let connectionType = null;

  // `.connection` is still experimental, so warning is raised for the call saying
  // property `Connection` does not exist on type `Navigator`.
  // Hence, currently ignoring it.
  // @ts-ignore
  if (window.navigator && window.navigator.connection) {
    // @ts-ignore
    connectionType = window.navigator.connection.type;
  }

  if (connectionType === undefined || connectionType === null) {
    connectionType = UNIDENTIFIED;
  }

  return connectionType;
};

export const getVideoMakerDetails = (
  questionSubject: string,
  grade: string
): IVideoMakerData | null => {
  const gradeAsNumber = Number(grade);

  if (questionSubject == "Maths") {
    if (gradeAsNumber >= 10) {
      return VideoMakerData[1];
    } else if (gradeAsNumber <= 6) {
      return VideoMakerData[2];
    } else if (gradeAsNumber >= 7 && gradeAsNumber <= 9) {
      return VideoMakerData[6];
    }
  }

  if (questionSubject == "Chemistry") {
    return VideoMakerData[3];
  }

  if (questionSubject == "Physics") {
    if (gradeAsNumber >= 10) {
      return VideoMakerData[4];
    } else if (gradeAsNumber >= 7 && gradeAsNumber <= 9) {
      return VideoMakerData[5];
    }
  }
  return null;
};
